import { SiteFooter } from "@/components/SiteFooter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Clock } from "lucide-react";
import { useState, useMemo } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useReservationModal } from "@/context/ReservationContext";
import { listPublishedMenu } from "@/lib/menu.functions";
import { mergeMenu, mergeMenuCategories } from "@/lib/menu-merge";
import cocktailsMenuBg from "@/assets/cocktails-menu-bg.jpg.asset.json";
import { useTrackEvent } from "@/lib/analytics";

const menuQuery = queryOptions({
  queryKey: ["public-menu"],
  queryFn: () => listPublishedMenu(),
  staleTime: 60_000,
});

export const Route = createFileRoute("/cocktails-menu")({
  loader: ({ context }) => context.queryClient.ensureQueryData(menuQuery),
  head: () => {
    const ogImage = "https://plunkets.ie/og-cocktails.jpg?v=2";
    return {
      meta: [
        { title: "Cocktail Bar Dublin City Centre — Cocktails & Menu | Plunkets Pub" },
        { name: "description", content: "The cocktail pub in Dublin city centre. 20+ cocktails, 2 for €18 all night Monday to Thursday, pitchers of beer (3.5 pints) and a proper pub kitchen menu at Plunkets, Middle Abbey Street, Dublin 1." },
        { name: "keywords", content: "cocktail bar Dublin, cocktail pub Dublin city centre, cocktails Dublin, 2 for €18 cocktails Dublin, cocktail menu Dublin, pub food Dublin city centre" },
        { property: "og:title", content: "Cocktail Bar in Dublin City Centre — Plunkets" },
        { property: "og:description", content: "20+ cocktails, 2 for €18 Mon–Thu, pitchers of beer, and a proper pub kitchen menu in Dublin city centre." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://plunkets.ie/cocktails-menu" },
        { property: "og:image", content: ogImage },
        { property: "og:image:secure_url", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:alt", content: "Cocktails at Plunkets Pub Dublin" },
        { property: "og:locale", content: "en_IE" },
        { property: "og:site_name", content: "Plunkets Pub Dublin" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Cocktail Bar in Dublin City Centre — Plunkets" },
        { name: "twitter:description", content: "20+ cocktails, 2 for €18 Mon–Thu at Plunkets Pub, Middle Abbey Street." },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: "https://plunkets.ie/cocktails-menu" }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Menu",
          name: "Plunkets Pub — Cocktails & Menu",
          url: "https://plunkets.ie/cocktails-menu",
          inLanguage: "en-IE",
          provider: {
            "@type": "BarOrPub",
            name: "Plunkets Pub",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Middle Abbey Street",
              addressLocality: "Dublin",
              postalCode: "D01",
              addressCountry: "IE",
            },
          },
        }),
      }],
    };
  },
  errorComponent: ({ error }) => (
    <main className="grid min-h-screen place-items-center bg-background p-6 text-center text-foreground">
      <p className="text-sm text-destructive">Menu unavailable: {error instanceof Error ? error.message : String(error)}</p>
    </main>
  ),
  notFoundComponent: () => <main className="grid min-h-screen place-items-center">Not found</main>,
  component: CocktailsPage,
});

function CocktailsPage() {
  useTrackEvent("menu_view", { menu_name: "cocktails", menu_slug: "cocktails-menu" });
  const { open: openModal } = useReservationModal();
  const { data: dbRows } = useSuspenseQuery(menuQuery);
  const items = useMemo(() => mergeMenu(dbRows), [dbRows]);
  const categories = useMemo(() => mergeMenuCategories(items), [items]);
  const [filter, setFilter] = useState<string>("All");

  const filtered = useMemo(() => {
    if (filter === "Plunkets Deals") {
      const dealPattern = /2\s*for\s*(1|€?18)|2in1|promo|deal|pitcher/i;
      return items.filter(
        (m) =>
          dealPattern.test(m.name) ||
          dealPattern.test(m.badge || "") ||
          dealPattern.test(m.slug) ||
          dealPattern.test(m.tagline),
      );
    }
    const list = filter === "All" ? items : items.filter((m) => m.category === filter);
    if (filter !== "All") return list;
    const catIdx = new Map(categories.map((c, i) => [c, i]));
    return [...list].sort((a, b) => {
      const ca = catIdx.get(a.category) ?? 999;
      const cb = catIdx.get(b.category) ?? 999;
      if (ca !== cb) return ca - cb;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
  }, [filter, items, categories]);

  const filters = ["All", "Plunkets Deals", ...categories];

  return (
    <main className="min-h-screen bg-background text-foreground">
      <section
        className="relative border-b border-border/60 px-5 pb-8 pt-10 md:px-8 md:pb-16 md:pt-24"
        style={{
          backgroundImage: `url(${cocktailsMenuBg.url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative z-10 mx-auto max-w-6xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft size={16} /> Back to Plunkets
          </Link>

          <p className="mt-6 text-[11px] uppercase tracking-[0.35em] text-primary md:mt-10 md:text-xs md:tracking-[0.4em]">Drink & Eat</p>
          <h1 className="mt-3 font-display text-3xl leading-[0.95] tracking-tight sm:text-4xl md:mt-4 md:text-6xl">
            Cocktails & <span className="text-gold-gradient">Menu</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base md:mt-6 md:text-lg">
            Around 20 cocktails poured by bartenders who take it seriously — alongside a proper pub kitchen menu.
            Tap any item to see ingredients, pairings and the story behind it.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs text-primary sm:px-4 sm:py-2 sm:text-sm md:mt-8">
            <Clock size={14} className="sm:hidden" /><Clock size={16} className="hidden sm:block" /> 2 cocktails for €18 all night · Mon–Thu
          </div>
        </div>
      </section>

      <section className="sticky top-0 z-30 border-b border-border/60 bg-background/85 py-3 backdrop-blur md:py-4">
        <div className="mx-auto flex max-w-6xl gap-2 overflow-x-auto px-5 [-ms-overflow-style:none] [scrollbar-width:none] md:px-8 [&::-webkit-scrollbar]:hidden">
          {filters.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={[
                  "whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-all",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card/30 text-muted-foreground hover:border-primary/40 hover:text-foreground",
                ].join(" ")}
              >
                {f}
              </button>
            );
          })}
        </div>
      </section>

      <section className="px-5 py-10 md:px-8 md:py-16">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {filtered.map((item) => (
            <Link
              key={item.slug}
              to="/menu/$slug"
              params={{ slug: item.slug }}
              className="group overflow-hidden rounded-2xl border border-border bg-card/30 transition-all hover:border-primary/50 hover:shadow-[0_20px_60px_-20px_rgba(198,164,122,0.35)]"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-muted/40">
                {item.image && (
                  <img
                    src={item.image}
                    alt={`${item.name} at Plunkets Pub Dublin`}
                    width={1024}
                    height={1024}
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                {item.badge && (
                  <span className="absolute left-3 top-3 rounded-full bg-primary/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground sm:left-4 sm:top-4 sm:px-3">
                    {item.badge}
                  </span>
                )}
                <span className="absolute right-3 top-3 rounded-full bg-background/85 px-2.5 py-1 text-sm font-bold text-primary backdrop-blur sm:right-4 sm:top-4 sm:px-3">
                  {item.price}
                </span>
              </div>
              <div className="p-4 sm:p-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{item.category}</p>
                <h3 className="mt-2 font-display text-lg font-bold leading-tight group-hover:text-primary sm:text-xl">
                  {item.name}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{item.tagline}</p>
                <span className="mt-3 inline-block text-xs font-semibold uppercase tracking-wider text-primary sm:mt-4">
                  View details →
                </span>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="mx-auto mt-12 max-w-md text-center text-muted-foreground">
            No items in this category yet.
          </p>
        )}
      </section>

      <section className="px-5 pb-20 md:px-8">
        <div className="mx-auto max-w-6xl rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-10">
          <h2 className="font-display text-2xl font-bold md:text-3xl">Reserve a spot</h2>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground md:text-base">
            Lock in your seat for happy hour, dinner or a late-night round.
          </p>
          <button
            onClick={() => openModal({ bookingType: "food_and_drinks" })}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground transition-all hover:opacity-90"
          >
            Book a Table
          </button>
        </div>
      </section>
    <SiteFooter />
    </main>
  );
}
